import { useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { useCreateScenarioLocation, useRenameScenarioLocation } from '../../../../features/scenarios';
import type { Scenario, ScenarioLocation } from '../types';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import styles from '../ScenarioGenerator.module.css';
import { patchSceneInLastResult } from './lastResultScenePatch';

type SceneLocationControlProps = {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
};

const LOCATION_KEY_PREFIX = 'loc';
const LOCATION_KEY_RANDOM_CHARS = 6;
// gitleaks:allow — 乱数 key 用の base36 文字集合。シークレットではないが高エントロピーとして誤検出されるため除外する
const LOCATION_KEY_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'; // gitleaks:allow
const SELECT_ADD_VALUE = '__add_location__';

/**
 * シナリオ内で一意な場面 key を生成する (ユーザーには見せない内部 ID。
 * docs/features/scene-location-and-production-mode-plan.md §2.1 「key は不変 ID」)。
 * 衝突時は再抽選し、極めて低確率の最終フォールバックとしてタイムスタンプを混ぜる。
 */
function generateLocationKey(existingKeys: Set<string>): string {
  for (let attempt = 0; attempt < 10; attempt++) {
    let suffix = '';
    for (let i = 0; i < LOCATION_KEY_RANDOM_CHARS; i++) {
      suffix += LOCATION_KEY_ALPHABET[Math.floor(Math.random() * LOCATION_KEY_ALPHABET.length)];
    }
    const key = `${LOCATION_KEY_PREFIX}${suffix}`;
    if (!existingKeys.has(key)) return key;
  }
  return `${LOCATION_KEY_PREFIX}${Date.now().toString(36)}`;
}

/**
 * サムネイル領域上部・場面継続／場面転換ドロップダウンの右に置く「場面」コントロール
 * (場面固有化 P1 step-5。SceneFrameCarousel から描画)。
 *
 * badge select 1個で「現在の場面の表示」と「付け替え」を兼ねる (顔あり/顔なし・
 * 場面継続/転換の既存バッジと同じ意匠。docs/features/
 * scene-location-and-production-mode-plan.md §2.3)。select の「+ 場面を追加」選択で
 * ポップオーバー式の追加フォームを開き、追加後はこのシーンへ即座に割り当てる。
 * 場面が選択中のときだけ隣に改名(name/description)用の鉛筆アイコンを出す
 * (key は不変・削除UIはP1では提供しない)。
 *
 * locations 未宣言の旧シナリオでも scenario.locations が空配列/undefined として
 * 扱われ、「場面未設定 + 追加」だけの状態で動く (§2.4 互換要件)。
 */
export function SceneLocationControl({ scenario, scene }: SceneLocationControlProps) {
  const { updateSceneMutation, lastResult, setLastResult } = useSceneHandlers();
  const createLocationMutation = useCreateScenarioLocation();
  const renameLocationMutation = useRenameScenarioLocation();
  const toast = useToast();

  const locations = scenario.locations ?? [];
  const currentLocation = locations.find((loc) => loc.key === scene.location_key) || null;

  const [mode, setMode] = useState<'view' | 'add' | 'rename'>('view');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // lastResult (ScenarioGenerator のローカル state) を直接更新する。scenario.locations は
  // シナリオ単位で全シーンの SceneItem インスタンスから共有参照されるため、対象シナリオの
  // 配列を丸ごと差し替えれば他シーンの表示にも反映される (continues_previous_shot /
  // has_visible_face の既存パターンと同じ)。
  const applyLocationsToLastResult = (nextLocations: ScenarioLocation[]) => {
    if (!lastResult) return;
    setLastResult((prev) => (prev ? {
      ...prev,
      scenarios: prev.scenarios.map((s: any) =>
        s.id === scenario.id ? { ...s, locations: nextLocations } : s
      ),
    } : prev));
  };

  const applySceneLocationKeyToLastResult = (locationKey: string | null | undefined) => {
    setLastResult((prev) => patchSceneInLastResult(prev, scene.id, { location_key: locationKey }));
  };

  /** シーンへ場面を割り当てる。呼び出し側が成否で分岐できるよう boolean を返す
   *  (追加直後の自動割り当てで、割り当て失敗時に誤って成功トーストを出さないため)。
   *  楽観更新: 選んだ瞬間に lastResult へ当て、保存に失敗したときだけ元の値へ戻す
   *  (顔あり/顔なしと同じ。応答待ちの間に select が古い値へ引き戻されるのを防ぐ)。 */
  const assignLocation = async (locationKey: string | null): Promise<boolean> => {
    if (!scene.id) return false;
    const previousKey = scene.location_key;
    applySceneLocationKeyToLastResult(locationKey);
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: { location_key: locationKey },
      });
      return true;
    } catch (error: any) {
      applySceneLocationKeyToLastResult(previousKey);
      const message = error?.response?.data?.error || '場面の割り当てに失敗しました';
      toast.error(message);
      return false;
    }
  };

  const handleSelectChange = (value: string) => {
    if (value === SELECT_ADD_VALUE) {
      setFormName('');
      setFormDescription('');
      setMode('add');
      return;
    }
    void assignLocation(value || null);
  };

  const handleStartRename = () => {
    if (!currentLocation) return;
    setFormName(currentLocation.name);
    setFormDescription(currentLocation.description);
    setMode('rename');
  };

  const handleCancel = () => {
    setMode('view');
    setFormName('');
    setFormDescription('');
  };

  const handleSaveAdd = async () => {
    const name = formName.trim();
    if (!name) {
      toast.error('場面の名前を入力してください');
      return;
    }
    const existingKeys = new Set(locations.map((loc) => loc.key));
    const key = generateLocationKey(existingKeys);
    setSaving(true);
    try {
      const { locations: nextLocations } = await createLocationMutation.mutateAsync({
        scenarioId: scenario.id,
        payload: { key, name, description: formDescription.trim() },
      });
      applyLocationsToLastResult(nextLocations);
      // 追加した場面をこのシーンへ即座に割り当てる (計画書 §2.3「追加後はそのシーンへ
      // 即座に割り当ててよい」)。場面自体の追加は成功しているのでフォームは閉じる。
      // 割り当てが失敗した場合は assignLocation 側が個別のエラートーストを出すため、
      // ここでの成功トーストは割り当てまで成功した場合だけに絞る (二重表示防止)。
      setMode('view');
      const assigned = await assignLocation(key);
      if (assigned) {
        toast.success('場面を追加しました');
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || '場面の追加に失敗しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRename = async () => {
    if (!currentLocation) return;
    const name = formName.trim();
    if (!name) {
      toast.error('場面の名前を入力してください');
      return;
    }
    setSaving(true);
    try {
      const { locations: nextLocations } = await renameLocationMutation.mutateAsync({
        scenarioId: scenario.id,
        locationKey: currentLocation.key,
        payload: { name, description: formDescription.trim() },
      });
      applyLocationsToLastResult(nextLocations);
      toast.success('場面を更新しました');
      setMode('view');
    } catch (error: any) {
      const message = error?.response?.data?.error || '場面の更新に失敗しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // タイトル/エンドカード (ブランドカット) は「場所」概念を持たないため対象外
  // (showFaceBadge など他の場面依存バッジと同じ除外方針)。
  if (scene.brand_cut_position) return null;

  const isFormOpen = mode === 'add' || mode === 'rename';

  return (
    <span
      /* 場面名が長いときに select の固有幅 (最長の option) で右列へはみ出さないよう、
         この span は縮められるようにし (minWidth 0)、select 側は上限幅+省略記号にする (.sceneLocationSelect)。
         鉛筆ボタンは縮めない。 */
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 2, minWidth: 0, maxWidth: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      <select
        className={`badge ${currentLocation ? 'badge-gray' : 'badge-muted'} ${styles.sceneLocationSelect}`}
        value={scene.location_key || ''}
        onChange={(e) => handleSelectChange(e.target.value)}
        disabled={updateSceneMutation.isPending}
        title={currentLocation?.description || '場面（撮影場所）を選択'}
      >
        <option value="">場面未設定</option>
        {locations.map((loc) => (
          <option key={loc.key} value={loc.key}>
            {loc.name || '(名称未設定)'}
          </option>
        ))}
        <option value={SELECT_ADD_VALUE}>+ 場面を追加</option>
      </select>
      {currentLocation && (
        <button
          type="button"
          className={styles.fieldEditButton}
          style={{ flexShrink: 0 }}
          onClick={handleStartRename}
          title="場面の名前・説明を編集"
        >
          <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
        </button>
      )}
      {isFormOpen && (
        <div
          className={styles.sceneLocationPopover}
          data-scene-inline-edit
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
        >
          <textarea
            className={styles.sceneLocationInput}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="場面名 (例: リビング)"
            rows={1}
            autoFocus
          />
          <textarea
            className={styles.sceneLocationInput}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="場面の説明 (任意)"
            rows={2}
          />
          <div className={styles.sceneLocationActions}>
            <button type="button" className={styles.inlineEditCancel} onClick={handleCancel} title="キャンセル (Esc)">
              キャンセル
            </button>
            <button
              type="button"
              className={styles.inlineEditSave}
              onClick={mode === 'add' ? handleSaveAdd : handleSaveRename}
              disabled={saving}
              title="保存"
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
